import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../store";
import { useSubscriptionWithReconnect } from "./useSubscriptionWithReconnect";
import { updateClubChecklistData } from "../store/clubChecklistSlice";
import type { UserChecklistProgress } from "../store/clubChecklistSlice";
import { onUpdateUserChecklistProgress } from "../graphql/subscriptions";

interface SubscriptionData {
  onUpdateUserChecklistProgress: UserChecklistProgress | null;
}

/**
 * Subscribes to checklist updates for the authenticated user.
 * Keeps Redux checklist state in sync across tabs/sessions via AppSync real-time updates.
 * The $owner variable scopes the subscription to the current user's record only,
 * preventing Admin users from receiving other users' checklist updates.
 */
export function useChecklistSubscription(): void {
  const dispatch = useDispatch<AppDispatch>();
  const owner = useSelector((state: RootState) => state.clubChecklist.data?.owner);

  const handleData = useCallback(
    (data: SubscriptionData) => {
      const updated = data?.onUpdateUserChecklistProgress;
      if (updated) {
        dispatch(updateClubChecklistData(updated));
      }
    },
    [dispatch]
  );

  useSubscriptionWithReconnect<SubscriptionData>({
    query: onUpdateUserChecklistProgress,
    variables: owner ? { owner } : undefined,
    onData: handleData,
    enabled: !!owner,
  });
}
